/**
 * src/shell/portal/components/RadicalTransparencyView.tsx
 *
 * Public Radical Transparency Ledger for herx.org (#/transparency).
 * High-contrast, scannable comparison table declaring protocol data boundaries.
 * Explicitly declares:
 *  - What HERX Stores (cryptographic keys, salted hashes, encrypted blobs)
 *  - What HERX Never Stores (passwords, address books, surveillance trackers, plaintext DMs)
 */

import React from 'react';
import { ShieldCheck, ShieldAlert, Check, X, ArrowLeft, Lock, Database, EyeOff, KeyRound } from 'lucide-react';

export interface RadicalTransparencyViewProps {
  onBack?: () => void;
}

export const RadicalTransparencyView: React.FC<RadicalTransparencyViewProps> = ({ onBack }) => {
  const STORED_ITEMS = [
    {
      category: 'Identity',
      data: 'Passkey Public Keys',
      format: 'secp256r1 / Ed25519 Public Key',
      purpose: 'Cryptographic authentication via WebAuthn hardware enclaves. Private keys never leave user devices.',
    },
    {
      category: 'Profile',
      data: 'Encrypted Sovereign Profile Blobs',
      format: 'AES-256-GCM / WebAuthn PRF',
      purpose: 'User bio, avatar URL, and preferences encrypted with client-derived keys. Unreadable by server.',
    },
    {
      category: 'Telemetry',
      data: 'Salted & Truncated IP Hashes',
      format: 'HMAC-SHA256 (12 hex chars)',
      purpose: 'DDoS rate limiting and sybil prevention. Rotated salts ensure hashes cannot be mapped back to IP.',
    },
    {
      category: 'Governance',
      data: 'Public Jury Dockets & Verdicts',
      format: 'Ed25519 Signed Ledger Entries',
      purpose: 'Community peer moderation rulings. Completely open and auditable to prevent shadowbanning.',
    },
    {
      category: 'Access',
      data: 'Signed Channel Invites',
      format: 'Scoped Cryptographic Tokens',
      purpose: 'Permission delegations for private groups. Zero contact scrapers or address book uploads required.',
    },
  ];

  const NEVER_STORED_ITEMS = [
    {
      category: 'Credentials',
      data: 'Plaintext Passwords & PINs',
      alternative: 'Hardware Passkeys Only',
      reason: 'No credential database to breach. Elimination of password stuffing and database exfiltration.',
    },
    {
      category: 'Personal Data',
      data: 'Address Books & Contact Scraping',
      alternative: 'Cryptographic Token Invites',
      reason: 'HERX will never request access to your mobile phone contacts, address book, or personal call logs.',
    },
    {
      category: 'Surveillance',
      data: 'Ad Trackers & Behavioral Replays',
      alternative: 'Zero Tracking Cookies',
      reason: 'No Meta Pixel, Google Analytics, Hotjar, IDFA, or cross-site tracking scripts. DNT is honored 100%.',
    },
    {
      category: 'Messaging',
      data: 'Plaintext Direct Message Content',
      alternative: 'End-to-End Double Ratchet',
      reason: 'Private chats are encrypted device-to-device. Host relays only see opaque encrypted ciphertext.',
    },
    {
      category: 'Location',
      data: 'Real-Time GPS Coordinates',
      alternative: 'Coarse Voluntary Geo-Pills',
      reason: 'No background geolocation tracking. Channel tags (e.g. #kl-hangout) are completely voluntary.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#07090e] text-white font-sans p-6 md:p-12 selection:bg-indigo-500 selection:text-white">
      {/* Top Header */}
      <div className="max-w-5xl mx-auto mb-10">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-gray-300 border border-white/10 transition"
          >
            <ArrowLeft size={14} /> Return to Portal
          </button>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[11px] font-mono font-bold uppercase tracking-wider">
                Public Transparency Ledger
              </span>
              <span className="text-gray-500 text-xs font-mono">v3.2 Spec</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black text-white tracking-tight">
              Data Sovereignty & Privacy Ledger
            </h1>
            <p className="text-sm text-gray-400 mt-1 max-w-2xl">
              HERX is engineered around cryptographic minimization. We explicitly contrast what the protocol stores against the surveillance data we refuse to touch.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-300 flex items-center gap-3">
            <ShieldCheck size={28} className="shrink-0 text-indigo-400" />
            <div>
              <div className="font-bold text-white">Immutable Protocol Guarantees</div>
              <div className="text-[11px] text-gray-400">100% Organic Reach &bull; Zero Surveillance Ads</div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Table 1: What HERX Stores */}
        <div className="bg-[#0d111a] border border-emerald-500/20 rounded-3xl p-6 shadow-xl relative overflow-hidden">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <Database size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                What HERX Stores <Check size={18} className="text-emerald-400" />
              </h2>
              <p className="text-xs text-emerald-400/80 font-mono">Cryptographically minimized state</p>
            </div>
          </div>

          <div className="space-y-3">
            {STORED_ITEMS.map((item, idx) => (
              <div
                key={idx}
                className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 transition text-xs"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white text-sm">{item.data}</span>
                  <span className="font-mono text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    {item.format}
                  </span>
                </div>
                <div className="text-gray-400 leading-relaxed text-[11px]">{item.purpose}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Table 2: What HERX Never Stores */}
        <div className="bg-[#0d111a] border border-red-500/20 rounded-3xl p-6 shadow-xl relative overflow-hidden">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
              <EyeOff size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                What HERX Never Stores <X size={18} className="text-red-400" />
              </h2>
              <p className="text-xs text-red-400/80 font-mono">Zero surveillance data collection</p>
            </div>
          </div>

          <div className="space-y-3">
            {NEVER_STORED_ITEMS.map((item, idx) => (
              <div
                key={idx}
                className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-red-500/30 transition text-xs"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white text-sm">{item.data}</span>
                  <span className="font-mono text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
                    {item.alternative}
                  </span>
                </div>
                <div className="text-gray-400 leading-relaxed text-[11px]">{item.reason}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cryptographic Proof Verification Card */}
      <div className="max-w-5xl mx-auto p-6 rounded-3xl bg-gradient-to-r from-indigo-950/40 via-purple-950/20 to-black border border-white/10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-indigo-300 shrink-0">
              <KeyRound size={24} />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Client-Side Verification Architecture</h3>
              <p className="text-xs text-gray-400 max-w-xl">
                All claims in this ledger are verifiable via open-source audits. HERX uses native WebAuthn (FIDO2) and W3C Web Cryptography APIs directly in your browser.
              </p>
            </div>
          </div>

          <a
            href="https://github.com/herx-org"
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold border border-white/10 transition whitespace-nowrap"
          >
            Inspect Source Audits &rarr;
          </a>
        </div>
      </div>
    </div>
  );
};
